import { useEffect, useRef } from 'react';

const BACKGROUND_COLOR = 'rgb(17, 17, 17)';
const COLORS = ['#A27B5C', '#8a6b4e', '#755c43'];

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

function ParticleBackground({ isEnabled = true }) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const context = canvas?.getContext('2d', { alpha: true });

    if (!canvas || !context) return undefined;

    const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const mobileQuery = window.matchMedia('(max-width: 768px), (pointer: coarse)');
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;

    let particles = [];
    let animationFrameId = null;
    let resizeFrameId = null;
    let lastFrameTime = 0;
    let canvasWidth = 0;
    let canvasHeight = 0;
    let simulation = null;

    class Particle {
      constructor() {
        this.x = Math.random() * canvasWidth;
        this.y = Math.random() * canvasHeight;
        this.mass = Math.random() * 7.5 + 0.5;
        this.radius = this.mass * 0.15;
        this.charge = Math.random() < 0.5 ? -1 : 1;
        this.color = COLORS[Math.floor(Math.random() * COLORS.length)];
        this.tail = [];
        this.fx = 0;
        this.fy = 0;
        this.vx = 0;
        this.vy = 0;
      }
    }

    const getSimulationSettings = () => {
      const isMobile = mobileQuery.matches;
      const hasLimitedCpu = Boolean(
        (navigator.hardwareConcurrency && navigator.hardwareConcurrency <= 4)
        || (navigator.deviceMemory && navigator.deviceMemory <= 4)
      );
      const saveData = Boolean(connection?.saveData);
      const prefersLessMotion = reducedMotionQuery.matches || saveData;
      const areaScale = Math.sqrt((canvasWidth * canvasHeight) / (1440 * 900));
      const baseCount = prefersLessMotion ? 22 : (isMobile ? 30 : (hasLimitedCpu ? 48 : 84));
      const minCount = prefersLessMotion ? 12 : (isMobile ? 18 : 36);
      const maxCount = prefersLessMotion ? 28 : (isMobile ? 36 : (hasLimitedCpu ? 58 : 108));

      return {
        shouldAnimate: isEnabled,
        particleCount: Math.round(clamp(baseCount * areaScale, minCount, maxCount)),
        tailLength: prefersLessMotion ? 3 : (isMobile ? 5 : (hasLimitedCpu ? 7 : 10)),
        frameInterval: 1000 / (prefersLessMotion ? 12 : (isMobile || hasLimitedCpu ? 20 : 30))
      };
    };

    const paintBackground = () => {
      context.fillStyle = BACKGROUND_COLOR;
      context.fillRect(0, 0, canvasWidth, canvasHeight);
    };

    const stopAnimation = () => {
      if (animationFrameId !== null) {
        cancelAnimationFrame(animationFrameId);
        animationFrameId = null;
      }
    };

    const initParticles = () => {
      particles = Array.from(
        { length: simulation?.shouldAnimate ? simulation.particleCount : 0 },
        () => new Particle()
      );
    };

    const updateAndDrawParticles = () => {
      paintBackground();

      for (let index = 0; index < particles.length; index += 1) {
        const particleA = particles[index];

        if (Math.random() < 0.08) particleA.charge = -particleA.charge;

        for (let otherIndex = index + 1; otherIndex < particles.length; otherIndex += 1) {
          const particleB = particles[otherIndex];
          const dx = particleB.x - particleA.x;
          const dy = particleB.y - particleA.y;
          const distanceSquared = Math.max(dx * dx + dy * dy, 0.01);
          const distance = Math.sqrt(distanceSquared) + 0.1;

          if (distance >= particleA.radius + particleB.radius) {
            const force = Math.min(8, (5.81 * particleA.mass * particleB.mass) / distanceSquared);
            const forceX = dx / distance;
            const forceY = dy / distance;

            particleA.fx += force * forceX * particleB.charge;
            particleA.fy += force * forceY * particleB.charge;
            particleB.fx -= force * forceX * particleA.charge;
            particleB.fy -= force * forceY * particleA.charge;
          }
        }
      }

      context.lineCap = 'round';
      context.lineJoin = 'round';

      particles.forEach((particle) => {
        particle.vx = (particle.vx + particle.fx) * 0.85;
        particle.vy = (particle.vy + particle.fy) * 0.85;
        particle.tail.unshift({ x: particle.x, y: particle.y });

        if (particle.tail.length > simulation.tailLength) particle.tail.pop();

        particle.x += particle.vx;
        particle.y += particle.vy;
        particle.fx = 0;
        particle.fy = 0;

        if (particle.x > canvasWidth + particle.radius) {
          particle.x = -particle.radius;
          particle.tail = [];
        } else if (particle.x < -particle.radius) {
          particle.x = canvasWidth + particle.radius;
          particle.tail = [];
        }

        if (particle.y > canvasHeight + particle.radius) {
          particle.y = -particle.radius;
          particle.tail = [];
        } else if (particle.y < -particle.radius) {
          particle.y = canvasHeight + particle.radius;
          particle.tail = [];
        }

        context.strokeStyle = particle.color;
        context.lineWidth = particle.radius * 2;
        context.beginPath();
        context.moveTo(particle.x, particle.y);
        particle.tail.forEach((point) => context.lineTo(point.x, point.y));
        context.stroke();
      });
    };

    const requestNextFrame = () => {
      if (
        animationFrameId === null
        && simulation?.shouldAnimate
        && !document.hidden
      ) {
        animationFrameId = requestAnimationFrame(animate);
      }
    };

    function animate(timestamp) {
      animationFrameId = null;

      if (!simulation?.shouldAnimate || document.hidden) return;

      if (timestamp - lastFrameTime >= simulation.frameInterval) {
        lastFrameTime = timestamp;
        updateAndDrawParticles();
      }

      requestNextFrame();
    }

    const refreshSimulation = () => {
      stopAnimation();
      simulation = getSimulationSettings();
      lastFrameTime = 0;
      paintBackground();
      initParticles();
      requestNextFrame();
    };

    const setCanvasSize = () => {
      canvasWidth = window.innerWidth;
      canvasHeight = window.innerHeight;

      const pixelRatio = Math.min(window.devicePixelRatio || 1, mobileQuery.matches ? 1 : 1.5);
      canvas.width = Math.round(canvasWidth * pixelRatio);
      canvas.height = Math.round(canvasHeight * pixelRatio);
      context.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
      refreshSimulation();
    };

    const handleResize = () => {
      if (resizeFrameId !== null) cancelAnimationFrame(resizeFrameId);
      resizeFrameId = requestAnimationFrame(() => {
        resizeFrameId = null;
        setCanvasSize();
      });
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        stopAnimation();
      } else {
        lastFrameTime = 0;
        requestNextFrame();
      }
    };

    const addMediaListener = (query, listener) => {
      if (query.addEventListener) query.addEventListener('change', listener);
      else query.addListener(listener);
    };

    const removeMediaListener = (query, listener) => {
      if (query.removeEventListener) query.removeEventListener('change', listener);
      else query.removeListener(listener);
    };

    setCanvasSize();
    window.addEventListener('resize', handleResize);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    addMediaListener(reducedMotionQuery, refreshSimulation);
    addMediaListener(mobileQuery, setCanvasSize);
    connection?.addEventListener?.('change', refreshSimulation);

    return () => {
      stopAnimation();
      if (resizeFrameId !== null) cancelAnimationFrame(resizeFrameId);
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      removeMediaListener(reducedMotionQuery, refreshSimulation);
      removeMediaListener(mobileQuery, setCanvasSize);
      connection?.removeEventListener?.('change', refreshSimulation);
      particles = [];
    };
  }, [isEnabled]);

  return (
    <canvas
      ref={canvasRef}
      id="particle-canvas"
      aria-hidden="true"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100vh',
        zIndex: 0,
        pointerEvents: 'none',
        background: BACKGROUND_COLOR,
        opacity: 1,
        transition: 'opacity 0.3s ease'
      }}
    />
  );
}

export default ParticleBackground;
